import { CalendarDaysIcon } from "@heroicons/react/24/outline";

import { ACCOUNT_DISPOSITION_STATUSES } from "shared/lib/constants/account";
import { clsxMerge, getCanonicalMeetingTime } from "shared/lib/helpers";
import { AccountDetailsStatusI, AccountI } from "shared/lib/interfaces/account";
import { PropsWithClassNameI } from "shared/lib/interfaces/ui";

import { LabelValueV2 } from "@/components/modules/pipeline/account-details/shared/label-value";
import { formatDateInZone } from "@/modules/pipeline/account-details/shared/contact-local-time";
import { DISQUALIFY_DISPOSITION_LABELS } from "@/modules/pipeline/account-details";
import { SetNextTouchTimeDropdown } from "./next-touch-time-dropdown";
import { CampaignI } from "@/lib/types/campaign";

interface AccountStatusDetailsPropsI extends PropsWithClassNameI {
  accountStatus: AccountDetailsStatusI;
  campaign?: CampaignI;
  account?: AccountI;
  /**
   * The lead's time zone. Dates show it by name, or the caller's zone when it
   * is null (GLE-8478). A meeting date uses the meeting's own zone first.
   */
  leadTimeZone?: string | null;
}

const {
  COLD_LEAD,
  NURTURE,
  MEETING_SCHEDULED,
  FEEDBACK_RECEIVED,
  DISQUALIFIED,
} = ACCOUNT_DISPOSITION_STATUSES;

export const AccountStatusDetails = ({
  className,
  accountStatus,
  campaign,
  account,
  leadTimeZone,
}: AccountStatusDetailsPropsI) => {
  // GLE-8478: the meeting shows in the invite's or the modal's zone, else the
  // lead's. Other dates, and the original meeting date, use the lead's zone.
  const meetingTimeZone = accountStatus?.meeting_timezone || leadTimeZone;

  const formatDateShort = (
    date: string | null | undefined,
    timeZone = leadTimeZone
  ) => formatDateInZone(date, "M/D, hh:mm A", timeZone) ?? "N/A";

  const formatDateLong = (
    date: string | null | undefined,
    timeZone = leadTimeZone
  ) => formatDateInZone(date, "MMM D, hh:mm A", timeZone) ?? "N/A";

  const {
    status,
    primary_contact_name: prospectName,
    last_conversation_time: lastConversationTime,
    next_touch_time: nextTouchTime,
    ae_name: aeName,
    meeting_join_link: meetingJoinLink,
    original_meeting_time: originalMeetingTime,
    dq_reason: disqualificationReason,
  } = accountStatus || {};

  // GLE-8044: coalesce BMCE.start_time → canonical_meeting_time → follow_up_time → meeting_time.
  // Per Mica's daily, this widget should read ICS-derived time first, OAuth fallback.
  const meetingTime = getCanonicalMeetingTime(accountStatus || {});

  if (!status || status === COLD_LEAD) {
    return null;
  }

  return (
    <div className={clsxMerge("relative flex flex-col gap-4", className)}>
      {status !== DISQUALIFIED && (
        <LabelValueV2 label="Prospect name" value={prospectName || "N/A"} />
      )}

      {status === NURTURE && (
        <>
          <LabelValueV2
            label="Last conversation time"
            value={formatDateShort(lastConversationTime)}
          />

          <div className="flex items-start justify-between">
            <LabelValueV2
              label="Next touch time"
              value={formatDateShort(nextTouchTime)}
            />

            <SetNextTouchTimeDropdown
              className="dropdown-end relative right-4 top-4"
              campaign={campaign}
              account={account}
            />
          </div>
        </>
      )}

      {status === MEETING_SCHEDULED && (
        <>
          <LabelValueV2 label="AE name" value={aeName || "N/A"} />
          <div data-testid="status-meeting-time">
            <LabelValueV2
              label="Meeting date"
              value={formatDateLong(meetingTime, meetingTimeZone)}
            />
          </div>

          {meetingJoinLink && (
            <a
              target="_blank"
              href={meetingJoinLink}
              rel="noreferrer"
              className={clsxMerge(
                "flex w-max items-center gap-2 rounded",
                "bg-[#E3EBFF] px-2 py-1 text-[#4474E3] typography-body-4-medium"
              )}
            >
              <CalendarDaysIcon className="h-4 w-4" />
              <span>Link to join meeting</span>
            </a>
          )}
        </>
      )}

      {status === FEEDBACK_RECEIVED && (
        <>
          <LabelValueV2
            label="AE name"
            value={aeName || "N/A"}
            valueClassName="typography-body-3-medium"
          />

          <div className="grid grid-cols-2">
            <LabelValueV2
              label="Meeting date"
              value={formatDateShort(meetingTime, meetingTimeZone)}
              valueClassName="typography-body-3-medium"
            />

            {originalMeetingTime && (
              <LabelValueV2
                label="Original meeting date"
                labelClassName="text-[#666]"
                value={
                  <span className="text-[#666] line-through">
                    {formatDateShort(nextTouchTime)}
                  </span>
                }
              />
            )}
          </div>
        </>
      )}

      {status === DISQUALIFIED && disqualificationReason && (
        <LabelValueV2
          label="Reason"
          value={DISQUALIFY_DISPOSITION_LABELS[disqualificationReason]}
        />
      )}
    </div>
  );
};
